import { PermissionEnum, User, UserTypeEnum } from '@sr-sdks/api-sdk-axios';
import { ALL_WORKSPACES } from '../../constant/Common';
import { DeepPartial } from '../../types/commonTypes';

export class AuthHelper {
  constructor(private currentUser: DeepPartial<User>) {}

  static forUser(currentUser: DeepPartial<User>) {
    return new AuthHelper(currentUser);
  }

  currentUserHasPermission(...requiredPermissions: string[]): boolean {
    // Any workspace Ids returned means they have permission
    return this.getWorkspaceIds(...requiredPermissions).length > 0;
  }

  currentUserHasPermissionOnWorkspace(
    workspaceId: string,
    ...requiredPermissions: string[]
  ): boolean {
    const workspaceIds = this.getWorkspaceIds(...requiredPermissions);

    // Internal users with wildcard access have permission to any workspace
    if (this.currentUserIsInternal && workspaceIds.includes(ALL_WORKSPACES))
      return true;

    // External users have permission only if the requested Id is in the list
    return workspaceIds.includes(workspaceId);
  }

  get currentUserIsExternal() {
    return !this.currentUserIsInternal;
  }

  // Canonical definition of this check is `isInternalUser` in
  // utils/helpers/common.ts. Deliberately not delegated to: that module
  // pulls in the Redux store and React, which this leaf class -- used in
  // 15+ places, some outside a component tree -- should not depend on.
  // Keep the two in step; they are one line each.
  get currentUserIsInternal() {
    return this.currentUser.userType === UserTypeEnum.Internal;
  }

  getWorkspaceIds(...requiredPermissions: string[]) {
    // Ensure userRoles is always an array
    let userRoles = Array.isArray(this.currentUser?.roles)
      ? this.currentUser.roles
      : [];

    if (requiredPermissions.length > 0) {
      // Filter to the userRoles which have the required permission
      userRoles = userRoles.filter((userRole) => {
        const permissionKeys: PermissionEnum[] = userRole?.role
          ?.permissionKeys as any;

        return permissionKeys?.some((key: PermissionEnum) =>
          requiredPermissions.includes(key),
        );
      });
    }

    return userRoles.map((ur) => ur?.workspaceId);
  }
}
